import { SENT } from './constants'

/**
 * patchTranslator
 * Wraps i18next.t so we can:
 *  - Detect keys that are unknown (no entry in enMap & translation result equals key)
 *  - Mark keys that exist in English but are missing in a secondary language (missingInSpanish)
 *  - Inject sentinel wrappers around returned strings so the DOM scanner can later identify them
 *    Format:  PREFIX + key + SEP + renderedValue + CLOSE  (see constants / SENT)
 *  - Schedule a deferred scan/layout when new keys / states are discovered (throttled externally)
 *
 * We intentionally only wrap when res is a string and not already decorated to avoid duplicating tags infinitely (Looks like the old solitaire winning animation and will eventually crash the page).
 */

export function patchTranslator(i18next, enMap, missingInSpanish, unknownTKeys, schedule) {
  if (!i18next?.t) return // nothing to patch
  const orig = i18next.t.bind(i18next)
  i18next.t = function patchedT(key, ...rest) {
    try {
      const res = orig(key, ...rest)
      if (typeof key !== 'string') return res // non-string keys (arrays, etc.) not handled here

      // Only schedule if in browser context (avoid issues in SSR / tests w/out window)
      const scheduleIfWindow = () => typeof window !== 'undefined' && setTimeout(schedule, 50)

      if (!enMap[key]) {
        // Key not in English map -> could be unknown or provided via defaultValue inline
        const defaultValue = extractDefaultValue(rest)
        if (res === key || res === undefined) {
          // i18next returns the key when missing and no default provided -> mark as unknown
          unknownTKeys.add(key)
          scheduleIfWindow()
          return res
        }
        // If a defaultValue produced an actual string, wrap to indicate "missing" (red badge later)
        if (defaultValue && typeof res === 'string' && !res.includes(SENT.MISS_PREFIX)) {
          scheduleIfWindow()
          return `${SENT.MISS_PREFIX}${key}${SENT.SEP}${res}${SENT.CLOSE}`
        }
        scheduleIfWindow()
      } else if (missingInSpanish.has(key) && typeof res === 'string' && !res.includes(SENT.ORANGE_PREFIX)) {
        // Key exists in English but is missing in Spanish -> mark with orange sentinel
        scheduleIfWindow()
        return `${SENT.ORANGE_PREFIX}${key}${SENT.SEP}${res}${SENT.CLOSE}`
      }
      return res
    } catch (err) {
      if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        // Fail safe diagnostic for dev only
        // eslint-disable-next-line no-console
        console.debug('[i18n overlay] translator patch error', err)
      }
      return orig(key, ...rest)
    }
  }
}

// Pull a default value from i18next.t arguments (string shorthand or { defaultValue })
function extractDefaultValue(rest) {
  if (!rest.length) return undefined
  const first = rest[0]
  if (typeof first === 'string') return first
  if (first && typeof first === 'object' && typeof first.defaultValue === 'string') return first.defaultValue
  return undefined
}
